// The application's icons: a small, fixed subset of Lucide (lucide-static 1.49.0), copied as path
// data so no icon library is shipped. Lucide is licensed under the ISC License:
//
//   Copyright (c) 2026 Lucide Icons and Contributors
//   Permission to use, copy, modify, and/or distribute this software for any purpose with or
//   without fee is hereby granted, provided that the above copyright notice and this permission
//   notice appear in all copies. THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL
//   WARRANTIES WITH REGARD TO THIS SOFTWARE.
//
// Icons are decorative: the control or text next to them carries the meaning.

type Shape =
  | { readonly d: string }
  | { readonly cx: number; readonly cy: number; readonly r: number }
  | { readonly x: number; readonly y: number; readonly w: number; readonly h: number };

const ICONS = {
  check: [{ d: 'M20 6 9 17l-5-5' }],
  x: [{ d: 'M18 6 6 18' }, { d: 'm6 6 12 12' }],
  minus: [{ d: 'M5 12h14' }],
  plus: [{ d: 'M5 12h14' }, { d: 'M12 5v14' }],
  camera: [
    {
      d: 'M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z',
    },
    { cx: 12, cy: 13, r: 3 },
  ],
  images: [
    { d: 'm22 11-1.296-1.296a2.4 2.4 0 0 0-3.408 0L11 16' },
    { d: 'M4 8a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2' },
    { x: 8, y: 2, w: 14, h: 14 },
  ],
  'chevron-left': [{ d: 'm15 18-6-6 6-6' }],
  /** Forward in a right-to-left screen. */
  'arrow-left': [{ d: 'm12 19-7-7 7-7' }, { d: 'M19 12H5' }],
  /** Back in a right-to-left screen. */
  'arrow-right': [{ d: 'M5 12h14' }, { d: 'm12 5 7 7-7 7' }],
  list: [
    { d: 'M3 5h.01' },
    { d: 'M3 12h.01' },
    { d: 'M3 19h.01' },
    { d: 'M8 5h13' },
    { d: 'M8 12h13' },
    { d: 'M8 19h13' },
  ],
  'cloud-check': [
    { d: 'm17 15-5.5 5.5L9 18' },
    { d: 'M5.516 16.07A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 3.501 7.327' },
  ],
  'cloud-off': [
    { d: 'M10.94 5.274A7 7 0 0 1 15.71 10h1.79a4.5 4.5 0 0 1 4.222 6.057' },
    { d: 'M18.796 18.81A4.5 4.5 0 0 1 17.5 19H9A7 7 0 0 1 5.79 5.78' },
    { d: 'm2 2 20 20' },
  ],
  refresh: [
    { d: 'M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8' },
    { d: 'M21 3v5h-5' },
    { d: 'M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16' },
    { d: 'M8 16H3v5' },
  ],
  alert: [
    { d: 'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3' },
    { d: 'M12 9v4' },
    { d: 'M12 17h.01' },
  ],
  clock: [{ cx: 12, cy: 12, r: 10 }, { d: 'M12 6v6l4 2' }],
  calendar: [{ d: 'M8 2v3' }, { d: 'M16 2v3' }, { x: 3, y: 3, w: 18, h: 18 }, { d: 'M3 9h18' }],
  pencil: [
    {
      d: 'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
    },
    { d: 'm15 5 4 4' },
  ],
  clipboard: [
    { x: 8, y: 2, w: 8, h: 4 },
    {
      d: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2',
    },
    { d: 'M12 11h4' },
    { d: 'M12 16h4' },
    { d: 'M8 11h.01' },
    { d: 'M8 16h.01' },
  ],
  user: [{ d: 'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' }, { cx: 12, cy: 7, r: 4 }],
  circle: [{ cx: 12, cy: 12, r: 10 }],
  undo: [{ d: 'M9 14 4 9l5-5' }, { d: 'M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11' }],
  file: [
    {
      d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
    },
    { d: 'M14 2v5a1 1 0 0 0 1 1h5' },
    { d: 'M16 13H8' },
    { d: 'M16 17H8' },
  ],
  home: [
    { d: 'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8' },
    {
      d: 'M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
    },
  ],
  users: [
    { d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' },
    { d: 'M16 3.128a4 4 0 0 1 0 7.744' },
    { d: 'M22 21v-2a4 4 0 0 0-3-3.87' },
    { cx: 9, cy: 7, r: 4 },
  ],
  more: [
    { cx: 12, cy: 12, r: 1 },
    { cx: 19, cy: 12, r: 1 },
    { cx: 5, cy: 12, r: 1 },
  ],
  settings: [
    {
      d: 'M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915',
    },
    { cx: 12, cy: 12, r: 3 },
  ],
  building: [
    { d: 'M10 12h4' },
    { d: 'M10 8h4' },
    { d: 'M14 21v-3a2 2 0 0 0-4 0v3' },
    { d: 'M6 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2' },
    { d: 'M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16' },
  ],
  'log-out': [
    { d: 'm16 17 5-5-5-5' },
    { d: 'M21 12H9' },
    { d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' },
  ],
  layers: [
    {
      d: 'M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z',
    },
    { d: 'M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12' },
    { d: 'M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17' },
  ],
  search: [{ cx: 11, cy: 11, r: 8 }, { d: 'm21 21-4.34-4.34' }],
  'circle-check': [{ cx: 12, cy: 12, r: 10 }, { d: 'm9 12 2 2 4-4' }],
  lock: [{ x: 3, y: 11, w: 18, h: 11 }, { d: 'M7 11V7a5 5 0 0 1 10 0v4' }],
} satisfies Record<string, readonly Shape[]>;

export type IconName = keyof typeof ICONS;

export function Icon({
  name,
  className,
}: {
  readonly name: IconName;
  readonly className?: string | undefined;
}) {
  const shapes: readonly Shape[] = ICONS[name];
  return (
    <svg
      className={className === undefined ? 'icon' : `icon ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {shapes.map((shape, index) =>
        'd' in shape ? (
          <path key={index} d={shape.d} />
        ) : 'r' in shape ? (
          <circle key={index} cx={shape.cx} cy={shape.cy} r={shape.r} />
        ) : (
          <rect key={index} x={shape.x} y={shape.y} width={shape.w} height={shape.h} rx="2" />
        ),
      )}
    </svg>
  );
}
